import React, { useMemo } from "react";
import cn from "classnames";
import { IWrappingInput } from "@types";
import BalloonItem from "./BalloonItem";

type BalloonValue = Record<string, number>;

interface Props {
    className?: string;
    balloons: IWrappingInput[];
    limit: number;
    value: BalloonValue;
    onChange: (next: BalloonValue) => void;
}

const getTotal = (value: BalloonValue) =>
    Object.values(value || {}).reduce((sum, v) => sum + (Number.isFinite(v) ? v : 0), 0);

const sanitizeValue = (value: BalloonValue) => {
    const next: BalloonValue = {};
    for (const [k, v] of Object.entries(value || {})) {
        const n = Math.max(0, Math.floor(Number(v) || 0));
        if (n > 0) next[k] = n;
    }
    return next;
};

const BalloonInstancePicker: React.FC<Props> = ({
    className,
    balloons,
    limit,
    value,
    onChange,
}) => {

    const safeLimit = Math.max(0, Math.floor(Number(limit) || 0));
    const safeValue = useMemo(() => sanitizeValue(value), [value]);
    const total = useMemo(() => getTotal(safeValue), [safeValue]);

    const setCount = (id: number, nextCount: number) => {
        const key = String(id);
        const next = { ...safeValue };
        const n = Math.max(0, Math.floor(nextCount));
        if (n <= 0) {
            delete next[key];
        } else {
            next[key] = n;
        }
        onChange(next);
    };

    return (
        <div className={cn("w-full", className)}>
            <div className="w-full flex flex-wrap -mx-2">
                {balloons.map((b) => {
                    const count = safeValue[String(b.id)] || 0;
                    const canInc = total < safeLimit;
                    const canDec = count > 0;
                    return (
                        <BalloonItem
                            key={b.id}
                            balloon={b}
                            count={count}
                            canInc={canInc}
                            canDec={canDec}
                            onDec={() => setCount(b.id, count - 1)}
                            onInc={() => setCount(b.id, count + 1)}
                        />
                    );
                })}
            </div>
            <div className="w-full px-1 mt-1">
                <p className="text-xs text-[#999]">{`${total}/${safeLimit}`}</p>
            </div>
        </div>
    );
};

export default BalloonInstancePicker;
